import type { FormElement } from '../model/form.types';
import type { CompiledFieldNode, CompiledForm, CompiledNode } from '../model/types';
import { fieldEntries, isTableElement, walkElements } from './field-walk';
import { migrateLegacyElements } from './legacy-migrations';

export function countFillableFields(elements: ReadonlyArray<FormElement>): number {
  return new Set(
    fieldEntries(migrateLegacyElements([...elements])).map(({ element }) => element.id)
  ).size;
}

// Each table counts once on top of the fields inside it, which is how the permissions footer reads.
export function countFieldsAndTables(elements: ReadonlyArray<FormElement>): number {
  const tableIds = new Set<string>();
  for (const { element } of walkElements(migrateLegacyElements([...elements]))) {
    if (isTableElement(element)) tableIds.add(element.id);
  }
  return countFillableFields(elements) + tableIds.size;
}

function extraRowFields(nodes: CompiledNode[]): CompiledFieldNode[] {
  return nodes.flatMap((node) => {
    if (node.kind !== 'table') return [];
    const nested = node.cells.flat().flatMap((cell) => (cell.node ? [cell.node] : []));
    const ownRows = node.extraRows.flatMap((row) =>
      row.cells.flatMap((cell) => (cell.node ? [cell.node] : []))
    );
    return [...ownRows, ...extraRowFields(nested)];
  });
}

// Read-only and disabled fields count: the subject sees them, they are just not editable now.
export function countShownFillableFields(form: CompiledForm): number {
  const matrixFields = new Set(
    form.fieldOrder.filter((fieldId) => form.fields[fieldId].state.visible)
  ).size;
  const addedRowFields = extraRowFields(form.nodes).filter((node) => node.state.visible).length;
  return matrixFields + addedRowFields;
}
